<script setup lang="ts">
withDefaults(
  defineProps<{
    tag?: string
    maxItemsDisplay?: number
  }>(),
  {
    tag: `ul`,
    maxItemsDisplay: 1,
  },
);

const emit = defineEmits<{
  enter: []
  scroll: []
}>();

const root = ref<HTMLElement>();
const carrousel = ref<HTMLElement>();
const showChevronLeft = ref(false);
const showChevronRight = ref(false);

const currentIndex = defineModel<number>();

// Debounce the controles update as it can be called many times during:
// • scroll
// • HTML nodes updates
// • window resize
const debouncesUpdateControls = useDebounceFn(updateControls, 100);

// We need a way to control the number of elements inside the default slot
// this.$slots.default isn't reactive
// => use MutationObserver
// https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver/MutationObserver
useMutationObserver(carrousel, debouncesUpdateControls, {
  childList: true,
  attributes: false,
  subtree: false,
});

// Observe the size of the carrousel to hide/display controls
// this is a better alternative than matchMedia
// https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia
// => we only specify our media query config in the CSS
// https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
useResizeObserver(carrousel, debouncesUpdateControls);

function getFirstVisibleElementIndex() {
  if (!carrousel.value || !carrousel.value?.children) return -1;
  const { left: parentLeft } = carrousel.value.getBoundingClientRect();
  const items = Array.from(carrousel.value.children);
  const firstVisibleElementIndex = items.findLastIndex((item) => {
    const { left: itemLeft } = item.getBoundingClientRect();
    return (parentLeft - itemLeft) >= -10;
  });
  return firstVisibleElementIndex;
}

function moveTo(direction: 1 | -1) {
  const displayedItems = getDisplayedItemsCount();
  const firstVisibleElementIndex = getFirstVisibleElementIndex();
  if (firstVisibleElementIndex !== -1)
    moveToItem(firstVisibleElementIndex + displayedItems * direction);
}

function moveToItem(itemIndex: number) {
  if (!carrousel.value || !carrousel.value?.children) return;
  const firstVisibleElementIndex = getFirstVisibleElementIndex();
  if (firstVisibleElementIndex === itemIndex) return;
  const items = carrousel.value.children;
  const lastItemIndex = items.length - 1;
  const currentSlideIndex = Math.max(0, Math.min(itemIndex, lastItemIndex));
  carrousel.value.scrollLeft = (items[currentSlideIndex] as HTMLElement).offsetLeft;
}

// “currentIndex” might change externally
// • add this watch to keep in sync
// • it shouldn't be called if the value stays the same after being set to the same value
watch(currentIndex, () => {
  if (typeof currentIndex.value !== `number`) return;
  moveToItem(currentIndex.value);
}, { immediate: true });

const itemsCount = ref(1);
const isInitScroll = ref(true);
function updateControls() {
  if (isInitScroll.value) {
    isInitScroll.value = false;
  }
  else {
    // /!\ This will emit on mounted also :/
    // Made a barbaric solution to prevent that
    emit('scroll');
  }
  if (!carrousel.value || !carrousel.value?.children) return;
  itemsCount.value = carrousel.value.children.length;
  const firstVisibleElementIndex = getFirstVisibleElementIndex();

  currentIndex.value = firstVisibleElementIndex;
  const displayedItems = getDisplayedItemsCount();
  showChevronLeft.value = firstVisibleElementIndex !== 0;
  showChevronRight.value = (carrousel.value?.children.length > displayedItems) && firstVisibleElementIndex !== carrousel.value?.children.length - displayedItems;
}

function getDisplayedItemsCount() {
  const style = window.getComputedStyle(root.value as HTMLElement);
  // --items will return a CSS expression: “min(3, 999)”
  // Parse it!
  const stringValue = style.getPropertyValue(`--items`);
  const result = /min\((\d+),\s?(\d+)\)/.exec(stringValue);
  if (!result) return 1;
  const [_, first, last] = result;
  if (typeof first !== `string` || typeof last !== `string`) return 1;
  const currentItems = Math.min(Number.parseInt(first), Number.parseInt(last));
  return currentItems;
}
// NOTE: we need to wrap VBtn in Div, otherwise they won't be positioned where we want :(
</script>

<template>
  <div
    ref="root"
    class="iad-carrousel group/carrousel relative [--items-responsive:1] md:[--items-responsive:2] lg:[--items-responsive:999]"
    @mouseenter="emit('enter')"
  >
    <component
      :is="tag"
      ref="carrousel"
      class="iad-carrousel__list overflow-auto grid gap-x-[var(--gutter)] grid-flow-col auto-cols-[var(--size)] snap-mandatory snap-x scroll-smooth h-full"
      @scroll.passive="updateControls"
    >
      <slot />
    </component>
    <div
      v-if="showChevronLeft"
      class="transition-opacity opacity-0 group-hover/carrousel:opacity-100 top-1/2 -translate-y-1/2 absolute left-3"
    >
      <IadBtn
        variant="outlined"
        icon
        @click="moveTo(-1)"
      >
        <Icon name="iad-icon:caret-left" />
      </IadBtn>
    </div>
    <div
      v-if="showChevronRight"
      class="transition-opacity opacity-0 group-hover/carrousel:opacity-100 top-1/2 -translate-y-1/2 absolute right-3"
    >
      <IadBtn
        variant="outlined"
        icon
        @click="moveTo(1)"
      >
        <Icon name="iad-icon:caret-right" />
      </IadBtn>
    </div>
    <ol
      class="absolute pointer-events-none bottom-xs inset-x-0 flex justify-center gap-2xs"
      :class="{ hidden: itemsCount < 2 }"
    >
      <li
        v-for="index in itemsCount"
        :key="index"
        class="h-[6px] w-[18px] border rounded-full transition-colors"
        :class="{
          'bg-white border-neutral': (index - 1) !== currentIndex,
          'bg-secondary border-white': (index - 1) === currentIndex,
        }"
      />
    </ol>
  </div>
</template>

<style scoped>
.iad-carrousel {
  --gutter: var(--carrousel-gutter, 0px);
  --items: min(v-bind(maxItemsDisplay), var(--items-responsive));
  --size: calc((100%  - var(--gutter) * (var(--items) - 1)) / var(--items));
}

.iad-carrousel__list {
  /*
    handle scrollbar in windows:
    • -ms-overflow-style isn't working anymore
    • hide scrollbar on firefox
  */
  scrollbar-width: none;
  /*
    use ::-webkit-scrollbar for edge/chrome on windows
    https://developer.mozilla.org/en-US/docs/Web/CSS/::-webkit-scrollbar
    https://www.digitalocean.com/community/tutorials/css-scrollbars
  */
  &::-webkit-scrollbar {
    display: none;
  }
}

.iad-carrousel__list :deep(> *) {
  @apply snap-start;
  /*
    needed for chrome
    rounding was cropping border
  */
  scroll-margin: 0.5px;
}
</style>
